import { isEmail } from "../../core/password"

export type AccountInputMode = "auto" | "email" | "phone"
export type ResolvedAccountType = "email" | "phone"
export type AccountInputValidationError =
  | "requireAccount"
  | "requireEmail"
  | "validEmail"
  | "requirePhoneCode"
  | "requirePhone"
  | "validPhone"

export type AccountInputValue = {
  account: string
  phoneCode: string
}

export type PhoneCodeOption = {
  value: string
  label: string
  searchText?: string
  isCurrent?: boolean
}

type PhoneLikeValue = {
  phoneCode: string
  phoneNumber?: string
  account?: string
}

const PHONE_INPUT_PATTERN = /^[\d\s()+-]+$/

export const resolveAccountType = (account: string, mode: AccountInputMode): ResolvedAccountType => {
  if (mode !== "auto") return mode
  const value = account.trim()
  return value && PHONE_INPUT_PATTERN.test(value) && normalizePhoneNumber(value) ? "phone" : "email"
}

export const getAccountValidationError = (
  value: AccountInputValue,
  mode: AccountInputMode
): AccountInputValidationError | undefined => {
  if (mode === "auto" && !value.account) return "requireAccount"

  const type = resolveAccountType(value.account, mode)
  if (type === "email") {
    if (!value.account) return "requireEmail"
    return isEmail(value.account) ? undefined : "validEmail"
  }

  if (!normalizePhoneNumber(value.phoneCode)) return "requirePhoneCode"
  if (!value.account) return "requirePhone"
  return normalizePhoneNumber(value.account) && PHONE_INPUT_PATTERN.test(value.account) ? undefined : "validPhone"
}

export const getAccountHasError = (value: AccountInputValue, mode: AccountInputMode): boolean =>
  getAccountValidationError(value, mode) !== undefined

export const normalizePhoneNumber = (value: string): string => value.replace(/\D/g, "")

export const composeCellphone = (phoneCode: string, phoneNumber: string): string =>
  `${normalizePhoneNumber(phoneCode)}-${normalizePhoneNumber(phoneNumber)}`

export const isSamePhone = (left: PhoneLikeValue, right: PhoneLikeValue): boolean =>
  normalizePhoneNumber(left.phoneCode) === normalizePhoneNumber(right.phoneCode) &&
  normalizePhoneNumber(left.phoneNumber ?? left.account ?? "") ===
    normalizePhoneNumber(right.phoneNumber ?? right.account ?? "")
